// Job picker options for the Job Allocation tab.
//
// Two sources are merged into one flat, searchable list:
//
//   - `role_eligible_jobs` from the What-If payload — jobs the API considers
//     eligible for that RACI role, carrying a skill match and a trustworthy rate.
//   - every job in the process's company — so a reassignment is never blocked
//     just because the eligibility list is thin.
//
// Both appear together rather than behind a toggle, but the label says which is
// which: picking outside the eligible set is allowed, not silent. Eligibility is
// per role, so this runs per role, not per task.
//
// Rates are deliberately left raw here (`hourlyRate` + `currencyType`). The
// eligible list is already USD and reproduces the payload's costs exactly, while
// a company job is quoted in its own currency — converting is the caller's job,
// where the live exchange rates are.

import { formatPercent } from "./whatIfFormat";

// A job the API never rated for this role. Distinct from a 0% match, which is a
// real assessment the API did make.
const NOT_ELIGIBLE = "not eligible";

const toText = (value) => String(value ?? "").trim();

const toNumber = (value, fallback = null) => {
  if (value == null || value === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

// What the option reads as in the list. The name comes first so typing it still
// filters, since the search matches against the whole label.
const describeJob = (job) => {
  if (job.isCurrent) return "current";
  if (!job.eligible) return NOT_ELIGIBLE;
  if (job.skillMatchPercent == null) return "eligible";
  return `${formatPercent(job.skillMatchPercent)} match`;
};

// Anything the user should look twice at before accepting: no skill overlap at
// all, or never assessed for this role.
const isJobFlagged = (job) =>
  Boolean(job) && !job.isCurrent && (!job.eligible || job.skillMatchPercent === 0);

const toOption = (job) => {
  const note = describeJob(job);
  return {
    ...job,
    value: String(job.jobId),
    label: `${job.jobName} — ${note}`,
    note,
    flagged: isJobFlagged(job),
  };
};

// Company jobs arrive from `normalizeJob`, which keeps the API's own spelling
// alongside its normalised keys, so both are accepted.
export const normalizeCompanyJob = (job) => {
  const jobId = toNumber(job?.job_id ?? job?.jobId ?? job?.id);
  if (jobId == null) return null;

  return {
    jobId,
    jobName: toText(job?.name ?? job?.job_name ?? job?.jobName) || `Job ${jobId}`,
    hourlyRate: toNumber(job?.hourlyRate ?? job?.hourly_rate),
    currencyType: toText(job?.currencyType ?? job?.currency_type) || "USD",
    staffCount: toNumber(job?.staff_count ?? job?.staffCount, 1),
  };
};

export const indexCompanyJobs = (jobs) => {
  const byId = new Map();
  (Array.isArray(jobs) ? jobs : []).forEach((job) => {
    const normalized = normalizeCompanyJob(job);
    if (normalized) byId.set(String(normalized.jobId), normalized);
  });
  return byId;
};

// Eligible jobs keep the order the normalizer sorted them into (current first,
// then best skill match). Company-only jobs follow, alphabetically — they are the
// fallback, so they should never push a genuine candidate down the list.
export function buildJobOptions(eligibleJobs, companyJobsById) {
  const eligible = (eligibleJobs || []).map((job) =>
    toOption({
      ...job,
      eligible: true,
      // The eligible list has no headcount of its own; the company record does.
      staffCount:
        job.staffCount ?? companyJobsById?.get(String(job.jobId))?.staffCount,
    }),
  );

  const seen = new Set(eligible.map((option) => option.value));

  const extra = [...(companyJobsById?.values() || [])]
    .filter((job) => !seen.has(String(job.jobId)))
    .sort((a, b) => a.jobName.localeCompare(b.jobName))
    .map((job) =>
      toOption({
        ...job,
        eligible: false,
        isCurrent: false,
        skillMatchPercent: null,
        // Rate stays raw and unconverted; see the module note.
        hourlyRateUsd: null,
      }),
    );

  return [...eligible, ...extra];
}
